import {Nut_Settings_User} from 'nutshell-core/settings-constants';

import {CHART_HERO_DIV_ID} from '../../../../../ui/reports/report-constants';

import {simulateLeadCardDrag} from './simulate-lead-card-drag';
import {simulateViewSwitcher} from './simulate-view-switcher';
import type {NutshellTourConfig} from './types';

// Illustration paths relative to public/include/illustrations/
const TOUR_ILLUSTRATIONS = {
    leadBoardPipelineStages: 'tours/lead-board-tour/pipeline-stages',
    leadBoardLeadCard: 'tours/lead-board-tour/lead-card',
    leadBoardViewSwitcher: 'tours/lead-board-tour/view-switcher',
    leadReportReportTypes: 'tours/lead-report-tour/report-types',
    leadReportAiReports: 'tours/lead-report-tour/ai-reports',
    leadReportChartDetails: 'tours/lead-report-tour/chart-details',
    leadReportDateRange: 'tours/lead-report-tour/date-range',
    leadReportFilters: 'tours/lead-report-tour/filters',
    leadReportQuickActions: 'tours/lead-report-tour/quick-actions',
    savedListLeadLists: 'tours/saved-list-tour/lead-lists',
    savedListListFilters: 'tours/saved-list-tour/list-filters',
    savedListFilterOptions: 'tours/saved-list-tour/filter-options',
    savedListDateRange: 'tours/saved-list-tour/date-range',
    savedListSavingLists: 'tours/saved-list-tour/saving-lists',
    savedListYourLists: 'tours/saved-list-tour/your-lists',
    marketingAutomationsAddPage: 'tours/marketing-automations-tour/add-page',
    marketingAutomationsEnterUrl: 'tours/marketing-automations-tour/enter-url',
    marketingAutomationsFollowUp: 'tours/marketing-automations-tour/follow-up',
    marketingAutomationsActionConfiguration:
        'tours/marketing-automations-tour/action-configuration',
    marketingAutomationsActivityLog: 'tours/marketing-automations-tour/activity-log',
    viewActivitiesSalesDashboard: 'tours/view-activities-tour/sales-dashboard',
    viewActivitiesActivitiesList: 'tours/view-activities-tour/activities-list',
    viewActivitiesFilterActivities: 'tours/view-activities-tour/filter-activities',
    viewActivitiesCalendarView: 'tours/view-activities-tour/calendar-view',
    viewActivitiesYourCalendar: 'tours/view-activities-tour/your-calendar',
    tourComplete: 'tours/tour-complete',
};

// Example tour configuration for storybook
export const EXAMPLE_TOUR_HEADER_ID = 'tour-header';
export const EXAMPLE_TOUR_BUTTONS_ID = 'tour-buttons';
export const EXAMPLE_TOUR_SIDEBAR_ID = 'tour-sidebar';
export const EXAMPLE_TOUR_CONTENT_ID = 'tour-content';
export const EXAMPLE_TOUR_CONFIG: NutshellTourConfig = {
    id: 'example-tour',
    steps: [
        {
            title: 'Welcome to the Tour',
            description:
                'Hello world! This is the beginning of our tour. If the positions seem broken for you, please follow the steps in the story docs to open the tour in an iframe.',
            anchor: {
                element: `#${EXAMPLE_TOUR_HEADER_ID}`,
                on: 'bottom',
            },
        },
        {
            title: 'Action Buttons',
            description:
                'Here you can click some awesome buttons! Click the Like button to like the tour and continue.',
            anchor: {
                element: `#${EXAMPLE_TOUR_BUTTONS_ID}`,
                on: 'bottom',
            },
        },
        {
            title: 'Important Sidebar',
            description: 'This sidebar contains important stuff to go places',
            anchor: {
                element: `#${EXAMPLE_TOUR_SIDEBAR_ID}`,
                on: 'right',
            },
        },
        {
            title: 'The Magic Zone',
            description: 'And here is where the magic happens! ✨',
            anchor: {
                element: `#${EXAMPLE_TOUR_CONTENT_ID}`,
                on: 'top',
            },
        },
        {
            title: 'Tour Complete',
            description: 'This is the last step of the tour. Thanks for following along!',
        },
    ],
};

// Tour configuration for the Lead Board onboarding flow
export const LEAD_TOUR_CONTENT_AREA_ID = 'lead-tour-content-area';
export const LEAD_BOARD_TOUR_VIEW_SWITCHER_ID = 'lead-board-tour-view-switcher';
export const LEAD_BOARD_TOUR_CARD_DATA_TARGET = 'lead-card';
export const LEAD_BOARD_TOUR_CONFIG: NutshellTourConfig = {
    id: 'lead-board-tour',
    steps: [
        {
            title: 'Pipeline stages',
            description:
                'These columns represent your sales stages. Drag leads across them as they progress.',
            anchor: {
                element: `#${LEAD_TOUR_CONTENT_AREA_ID}`,
                on: 'left-start',
            },
            illustration: TOUR_ILLUSTRATIONS.leadBoardPipelineStages,
            onShow: simulateLeadCardDrag,
        },
        {
            title: 'Lead card',
            description:
                'Each lead is listed with their associated value, contact information, and AI summaries of the progress made so far.',
            anchor: {
                element: `[data-tour-target="${LEAD_BOARD_TOUR_CARD_DATA_TARGET}"]`,
                on: 'right',
            },
            illustration: TOUR_ILLUSTRATIONS.leadBoardLeadCard,
        },
        {
            title: 'View switcher',
            description:
                'Great job! You can also work with leads on a map, in a chart, or in a simple list format.',
            anchor: {
                element: `#${LEAD_TOUR_CONTENT_AREA_ID}`,
                on: 'left-start',
            },
            extraHighlights: [`#${LEAD_BOARD_TOUR_VIEW_SWITCHER_ID}`],
            illustration: TOUR_ILLUSTRATIONS.leadBoardViewSwitcher,
            onShow: simulateViewSwitcher,
        },
        {
            title: 'Great job!',
            description:
                "You are ready to manage your leads using Nutshell's lead board. Now, let's move on to the next task!",
            illustration: TOUR_ILLUSTRATIONS.tourComplete,
        },
    ],
    completionSetting: Nut_Settings_User.SETTING_TOUR_COMPLETED_LEAD_BOARD,
};

// Tour configuration for creating a saved list
export const SAVED_LIST_TOUR_SAVED_LISTS_ID = 'saved-list-tour-saved-lists';
export const SAVED_LIST_TOUR_FILTER_BUTTON_ID = 'saved-list-tour-filter-button';
export const SAVED_LIST_TOUR_CLOSE_DATE_SECTION_ID = 'saved-list-tour-close-date-section';
export const SAVED_LIST_TOUR_NEXT_FILTER_ID = 'saved-list-tour-next-filter-id';
export const SAVED_LIST_TOUR_SAVE_BUTTON_ID = 'saved-list-tour-save-button';
export const SAVED_LIST_TOUR_YOUR_LISTS_ID = 'saved-list-tour-your-lists';
export const SAVED_LIST_TOUR_CONFIG: NutshellTourConfig = {
    id: 'saved-list-tour',
    steps: [
        {
            title: 'Lead lists',
            description:
                'Let’s create a list of leads filtered for those closing in the next month.',
            anchor: {
                element: `#${SAVED_LIST_TOUR_SAVED_LISTS_ID}`,
                on: 'right',
            },
            illustration: TOUR_ILLUSTRATIONS.savedListLeadLists,
        },
        {
            title: 'Open filters',
            anchor: {
                element: `#${SAVED_LIST_TOUR_FILTER_BUTTON_ID}`,
                on: 'bottom',
            },
            description: 'Filters help you narrow down a list using specific parameters.',
            requiredAction: {
                guidanceText: 'Click the filter icon',
            },
            illustration: TOUR_ILLUSTRATIONS.savedListListFilters,
        },
        {
            title: 'Filter options',
            anchor: {
                element: `#${SAVED_LIST_TOUR_CLOSE_DATE_SECTION_ID}`,
                on: 'right',
            },
            description: 'Many filters offer options to narrow down your results.',
            requiredAction: {
                guidanceText: 'Select ‘Close date’',
            },
            illustration: TOUR_ILLUSTRATIONS.savedListFilterOptions,
        },
        {
            title: 'Date range',
            description: 'Set specific time frames to help you find specific leads.',
            anchor: {
                element: `#${SAVED_LIST_TOUR_CLOSE_DATE_SECTION_ID}`,
                on: 'right',
            },
            requiredAction: {
                guidanceText: 'Choose ‘In the next’ to see leads closing in the next 4 weeks',
            },
            illustration: TOUR_ILLUSTRATIONS.savedListDateRange,
        },
        {
            title: 'Saving lists',
            description: 'After applying filters, save them as a list you can visit later.',
            requiredAction: {
                guidanceText: 'Click ‘Save list’',
            },
            anchor: {
                element: `#${SAVED_LIST_TOUR_SAVE_BUTTON_ID}`,
                on: 'bottom',
            },
            illustration: TOUR_ILLUSTRATIONS.savedListSavingLists,
        },
        {
            title: 'Your lists',
            description: 'Moving forward, you’ll find all your saved lists here.',
            anchor: {
                element: `#${SAVED_LIST_TOUR_YOUR_LISTS_ID}`,
                on: 'right',
            },
            illustration: TOUR_ILLUSTRATIONS.savedListYourLists,
        },
        {
            title: 'You’re ready!',
            description: 'Great job! You’ve seen how you can save lists and access them later.',
            illustration: TOUR_ILLUSTRATIONS.tourComplete,
        },
    ],
    completionSetting: Nut_Settings_User.SETTING_TOUR_COMPLETED_SAVED_LIST,
};

// Tour configuration for creating lead reports
export const LEAD_REPORT_TOUR_SIDEBAR_ID = 'lead-report-tour-sidebar';
export const LEAD_REPORT_TOUR_CREATE_REPORT_ID = 'lead-report-tour-create-report';
export const LEAD_REPORT_TOUR_DATE_RANGE_ID = 'report-date-range-picker';
export const LEAD_REPORT_TOUR_FILTER_OPTIONS_ID = 'filter-drawer';
export const LEAD_REPORT_TOUR_SAVE_REPORT_ID = 'page-title-bar-buttons';
export const LEAD_REPORT_TOUR_CONFIG: NutshellTourConfig = {
    id: 'lead-report-tour',
    steps: [
        {
            title: 'Report types',
            description:
                'Straightforward analytics delivered out of the box by utilizing AI or by customizing your own.',
            anchor: {
                element: `#${LEAD_REPORT_TOUR_SIDEBAR_ID}`,
                on: 'right',
            },
            illustration: TOUR_ILLUSTRATIONS.leadReportReportTypes,
        },
        {
            title: 'AI-driven reports',
            description:
                'Describe your report requirements or get a quick start with our sample prompts.',
            requiredAction: {
                guidanceText: 'Select ‘Where are my top leads coming from?’ and press Enter.',
            },
            anchor: {
                element: `#${LEAD_REPORT_TOUR_CREATE_REPORT_ID}`,
                on: 'bottom',
            },
            illustration: TOUR_ILLUSTRATIONS.leadReportAiReports,
        },
        {
            title: 'Chart details',
            description: 'Hover over the resulting charts to see more details.',
            anchor: {
                element: `#${CHART_HERO_DIV_ID}`,
                on: 'right',
            },
            illustration: TOUR_ILLUSTRATIONS.leadReportChartDetails,
        },
        {
            title: 'Date range',
            description: 'Review your data while keeping a specific date range in mind.',
            anchor: {
                element: `#${LEAD_REPORT_TOUR_DATE_RANGE_ID}`,
                on: 'right',
            },
            illustration: TOUR_ILLUSTRATIONS.leadReportDateRange,
        },
        {
            title: 'Filters',
            description: 'Make your reports clearer by adding custom filters for better insights.',
            anchor: {
                element: `#${LEAD_REPORT_TOUR_FILTER_OPTIONS_ID}`,
                on: 'right',
            },
            illustration: TOUR_ILLUSTRATIONS.leadReportFilters,
        },
        {
            title: 'Quick actions',
            description: 'Save, share, and download your reports anytime.',
            anchor: {
                element: `#${LEAD_REPORT_TOUR_SAVE_REPORT_ID}`,
                on: 'bottom',
            },
            illustration: TOUR_ILLUSTRATIONS.leadReportQuickActions,
        },
        {
            title: 'Great job!',
            description:
                'You are ready to report on your leads. Now, let’s move on to the next task!',
            illustration: TOUR_ILLUSTRATIONS.tourComplete,
        },
    ],
    completionSetting: Nut_Settings_User.SETTING_TOUR_COMPLETED_LEAD_REPORTS,
};

// Tour configuration for creating marketing automations
export const MARKETING_AUTOMATIONS_TOUR_ADD_PAGE_ID = 'marketing-automations-tour-add-page';
export const MARKETING_AUTOMATIONS_TOUR_ENTER_URL_ID = 'marketing-automations-tour-enter-url';
export const MARKETING_AUTOMATIONS_TOUR_FOLLOW_UP_ID = 'marketing-automations-tour-follow-up';
export const MARKETING_AUTOMATIONS_TOUR_ACTIVITY_LOG_ID = 'marketing-automations-tour-activity-log';
export const MARKETING_AUTOMATIONS_TOUR_ACTION_CONFIGURATION_ID =
    'marketing-automations-tour-action-configuration';
export const MARKETING_AUTOMATIONS_TOUR_CONFIG: NutshellTourConfig = {
    id: 'marketing-automations-tour',
    redirectToExploreOnComplete: false,
    steps: [
        {
            title: 'Add new automation',
            description: 'Create a new automation by choosing a page from your website.',
            anchor: {
                element: `#${MARKETING_AUTOMATIONS_TOUR_ADD_PAGE_ID}`,
                on: 'bottom',
            },
            requiredAction: {
                guidanceText: 'Click ‘Add new automation’',
            },
            illustration: TOUR_ILLUSTRATIONS.marketingAutomationsAddPage,
        },
        {
            title: 'Enter the web page’s URL',
            description: 'Actions will run when visitors land on this page.',
            anchor: {
                element: `#${MARKETING_AUTOMATIONS_TOUR_ENTER_URL_ID}`,
                on: 'right',
            },
            requiredAction: {
                guidanceText: 'Enter a URL and automation name',
            },
            illustration: TOUR_ILLUSTRATIONS.marketingAutomationsEnterUrl,
        },
        {
            title: 'Automation',
            description:
                'Add the actions and notifications you want to run automatically after visitors land on this page.',
            anchor: {
                element: `#${MARKETING_AUTOMATIONS_TOUR_FOLLOW_UP_ID}`,
                on: 'left-start',
            },
            requiredAction: {
                guidanceText: 'Click ‘Add an action’',
            },
            illustration: TOUR_ILLUSTRATIONS.marketingAutomationsFollowUp,
        },
        {
            title: 'Action configuration',
            description: 'Configure action details and additional automations.',
            anchor: {
                element: `#${MARKETING_AUTOMATIONS_TOUR_ACTION_CONFIGURATION_ID}`,
                on: 'left-start',
            },
            requiredAction: {
                guidanceText: 'Select ‘Add a tag to a person’, select or create a tag, and save.',
            },
            illustration: TOUR_ILLUSTRATIONS.marketingAutomationsActionConfiguration,
        },
        {
            title: 'Page activity',
            description: 'Review who triggered this page’s automations in the page activity.',
            anchor: {
                element: `#${MARKETING_AUTOMATIONS_TOUR_ACTIVITY_LOG_ID}`,
                on: 'bottom-start',
            },
            illustration: TOUR_ILLUSTRATIONS.marketingAutomationsActivityLog,
        },
        {
            title: 'Great job!',
            description:
                'You’ve just created a marketing automation! Click ‘Test automation’ to see how it performs, or continue editing your automation.',
            illustration: TOUR_ILLUSTRATIONS.tourComplete,
        },
    ],
};

// Tour configuration for the activities dashboard
export const VIEW_ACTIVITIES_TOUR_NAV_ID = 'for-intercom-sales';
export const VIEW_ACTIVITIES_TOUR_SIDEBAR_ID = 'view-activities-tour-sidebar';
export const VIEW_ACTIVITIES_TOUR_FILTERS_ID = 'view-activities-tour-filters';
export const VIEW_ACTIVITIES_TOUR_CALENDAR_BUTTON_ID = 'view-activities-tour-calendar-button';
export const VIEW_ACTIVITIES_TOUR_CALENDAR_ID = 'view-activities-tour-calendar';
export const VIEW_ACTIVITIES_TOUR_CONFIG: NutshellTourConfig = {
    id: 'view-activities-tour',
    steps: [
        {
            title: 'Sales dashboard',
            description: 'To see your activities, navigate to the sales dashboard.',
            anchor: {
                element: `#${VIEW_ACTIVITIES_TOUR_NAV_ID}`,
                on: 'right',
            },
            illustration: TOUR_ILLUSTRATIONS.viewActivitiesSalesDashboard,
        },
        {
            title: 'Activities list',
            description:
                'This list shows activities assigned to you. Any activities synced from your calendar will show up here as well.',
            anchor: {
                element: `#${VIEW_ACTIVITIES_TOUR_SIDEBAR_ID}`,
                on: 'left',
            },
            illustration: TOUR_ILLUSTRATIONS.viewActivitiesActivitiesList,
        },
        {
            title: 'Filter your activities',
            description: 'Use these filters to narrow down your activity list.',
            anchor: {
                element: `#${VIEW_ACTIVITIES_TOUR_FILTERS_ID}`,
                on: 'left',
            },
            requiredAction: {
                guidanceText: 'Select ‘Scheduled’ from the status dropdown',
            },
            illustration: TOUR_ILLUSTRATIONS.viewActivitiesFilterActivities,
        },
        {
            title: 'Calendar view',
            description: 'The calendar gives you a quick overview of your upcoming schedule.',
            anchor: {
                element: `#${VIEW_ACTIVITIES_TOUR_CALENDAR_BUTTON_ID}`,
                on: 'left',
            },
            requiredAction: {
                guidanceText: 'Click the calendar icon to open it',
            },
            illustration: TOUR_ILLUSTRATIONS.viewActivitiesCalendarView,
        },
        {
            title: 'Your calendar',
            description: 'Click any day to jump to that day’s activities.',
            anchor: {
                element: `#${VIEW_ACTIVITIES_TOUR_CALENDAR_ID}`,
                on: 'left',
            },
            illustration: TOUR_ILLUSTRATIONS.viewActivitiesYourCalendar,
        },
        {
            title: 'You’re ready!',
            description: 'Great job! You now know how to view and manage your activities.',
            illustration: TOUR_ILLUSTRATIONS.tourComplete,
        },
    ],
    completionSetting: Nut_Settings_User.SETTING_TOUR_COMPLETED_VIEW_ACTIVITIES,
};
